import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, AlertCircle, ArrowRight, Globe, BarChart3, Clock, Shield } from "lucide-react";
import LeadForm from "@/forms/engine/LeadForm";
import quoteOnline, { onlineQuoteContext } from "@/forms/definitions/quoteOnline";
import { useToolLifecycle } from "@/utils/trackingHooks";

const OQ_ITEM_NAME = "online_quote";
const OQ_ITEM_OPTS = { itemType: "quote_tool" };
import {
  ONLINE_SELL_RATES, CARD_BANDS,
  FIXED_MONTHLY, DEFAULT_MIX_ONLINE, calculateProcessing,
} from "@/utils/pricingEngine";

function formatGBP(val) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(val);
}

function formatNumber(val) {
  return new Intl.NumberFormat("en-GB").format(Math.round(val));
}

function calculateOnlineQuote(inputs) {
  const { monthlyTurnover, averageTransactionValue, cardMixMode } = inputs;
  if (monthlyTurnover <= 0 || averageTransactionValue <= 0) return null;

  const mix = cardMixMode === "advanced"
    ? CARD_BANDS.reduce((acc, b) => ({ ...acc, [b.key]: parseFloat(inputs[`mix_${b.key}`]) || 0 }), {})
    : DEFAULT_MIX_ONLINE;

  const result = calculateProcessing(monthlyTurnover, averageTransactionValue, mix, ONLINE_SELL_RATES, { includeGateway: true });
  if (!result) return null;

  const pciCost = FIXED_MONTHLY.pci;
  const estimatedMonthlyCost = result.totalProcessing + pciCost;

  return {
    transactionCount: result.txnCount, processingCost: result.totalProcessing, pciCost,
    estimatedMonthlyCost, estimatedAnnualCost: estimatedMonthlyCost * 12,
  };
}

function CurrencyInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-brand-black mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-grey text-sm">£</span>
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" className="w-full pl-8 pr-4 py-3 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} />
      </div>
    </div>
  );
}

function PercentInput({ label, name, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-brand-black mb-1">{label}</label>
      <div className="relative">
        <input type="number" name={name} value={value} onChange={onChange} placeholder={placeholder} step="any" min="0" max="100" className="w-full pl-4 pr-8 py-2.5 rounded-xl border border-brand-light-grey bg-white text-brand-black text-sm focus:outline-none focus:ring-2 focus:ring-brand-aqua/30 focus:border-brand-aqua transition-all" data-testid={testId} />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-grey text-sm">%</span>
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, label, value, variant = "default", testId }) {
  const bg = { default: "bg-white border border-brand-light-grey", primary: "bg-brand-aqua-light border border-brand-aqua/20", annual: "bg-gradient-to-br from-brand-aqua-light to-white border border-brand-aqua/30" };
  const text = { default: "text-brand-black", primary: "text-brand-aqua-dark", annual: "text-brand-aqua-dark" };
  return (
    <div className={`${bg[variant]} rounded-xl p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-brand-grey" />}
        <p className="text-xs font-bold uppercase tracking-wider text-brand-grey">{label}</p>
      </div>
      <p className={`text-xl sm:text-2xl font-heading font-bold ${text[variant]}`}>{value}</p>
    </div>
  );
}

function ResultsPanel({ results }) {
  return (
    <div className="space-y-4 pt-2">
      <div className="grid grid-cols-2 gap-3">
        <ResultCard icon={BarChart3} label="Estimated monthly cost" value={formatGBP(results.estimatedMonthlyCost)} variant="primary" testId="ol-monthly-cost" />
        <ResultCard icon={BarChart3} label="Estimated annual cost" value={formatGBP(results.estimatedAnnualCost)} variant="annual" testId="ol-annual-cost" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <ResultCard label="Processing & gateway fees" value={formatGBP(results.processingCost)} testId="ol-processing" />
        <ResultCard icon={Shield} label="PCI compliance" value={formatGBP(results.pciCost)} testId="ol-pci" />
        <ResultCard icon={Clock} label="Monthly transactions" value={formatNumber(results.transactionCount)} testId="ol-transactions" />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-1">
        <Link to="/contact" className="flex-1 bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="ol-cta-quote">Get my tailored quote <ArrowRight size={16} /></Link>
        <a href="/book" className="flex-1 bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="ol-cta-speak">Speak to the team</a>
      </div>
    </div>
  );
}

// Pre-filled starting values; the capture form treats unchanged ones as defaults.
const INITIAL_INPUTS = {
  monthlyTurnover: "50000", averageTransactionValue: "50",
  cardMixMode: "default",
  mix_debit: "", mix_credit: "", mix_business_debit: "", mix_business_credit: "", mix_international: "", mix_amex: "",
};

function useOnlineQuote() {
  const [inputs, setInputs] = useState(INITIAL_INPUTS);
  const { markStarted, markCompleted } = useToolLifecycle(OQ_ITEM_NAME, OQ_ITEM_OPTS);

  const handleChange = (e) => {
    markStarted();
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const mixTotal = useMemo(() => {
    if (inputs.cardMixMode !== "advanced") return 100;
    return CARD_BANDS.reduce((sum, b) => sum + (parseFloat(inputs[`mix_${b.key}`]) || 0), 0);
  }, [inputs]);

  const mixError = inputs.cardMixMode === "advanced" && mixTotal !== 100;

  const parsed = useMemo(() => ({
    monthlyTurnover: parseFloat(inputs.monthlyTurnover) || 0,
    averageTransactionValue: parseFloat(inputs.averageTransactionValue) || 0,
    cardMixMode: inputs.cardMixMode,
    ...CARD_BANDS.reduce((acc, b) => ({ ...acc, [`mix_${b.key}`]: inputs[`mix_${b.key}`] }), {}),
  }), [inputs]);

  const results = useMemo(() => {
    if (mixError) return null;
    return calculateOnlineQuote(parsed);
  }, [parsed, mixError]);

  useEffect(() => { if (results) markCompleted(); }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

  return { inputs, handleChange, setInputs, mixTotal, mixError, results };
}

export default function OnlineQuoteBuilder({ className = "" }) {
  const { inputs, handleChange, setInputs, mixTotal, mixError, results } = useOnlineQuote();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const hasMinInput = parseFloat(inputs.monthlyTurnover) > 0 && parseFloat(inputs.averageTransactionValue) > 0;
  const leadContext = useMemo(() => onlineQuoteContext(inputs, results, INITIAL_INPUTS), [inputs, results]);

  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="online-quote-builder">
      <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
        <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><Globe size={20} className="text-white" /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-brand-black">Get an instant online payment estimate</h3>
          <p className="text-xs text-brand-grey">Estimate your likely monthly cost for online card processing</p>
        </div>
      </div>

      <div className="p-6 sm:p-8 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <CurrencyInput label="Monthly online card turnover" name="monthlyTurnover" value={inputs.monthlyTurnover} onChange={handleChange} placeholder="50000" testId="ol-turnover" />
          <CurrencyInput label="Average order value" name="averageTransactionValue" value={inputs.averageTransactionValue} onChange={handleChange} placeholder="50" testId="ol-avg-tx" />
        </div>

        <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-sm font-semibold text-brand-aqua hover:text-brand-aqua-dark transition-colors" data-testid="ol-mix-toggle">
          {inputs.cardMixMode === "advanced" ? "Use standard card mix" : "Enter my own card mix"}
          <ChevronDown size={16} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>

        {showAdvanced && (
          <div className="bg-gray-50 rounded-xl p-4 border border-brand-light-grey/50 space-y-3">
            <div className="flex gap-2">
              <button onClick={() => setInputs((p) => ({ ...p, cardMixMode: "default" }))} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.cardMixMode === "default" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="ol-mix-default">Standard</button>
              <button onClick={() => setInputs((p) => ({ ...p, cardMixMode: "advanced" }))} className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${inputs.cardMixMode === "advanced" ? "bg-brand-aqua text-white" : "bg-white border border-brand-light-grey text-brand-grey"}`} data-testid="ol-mix-advanced">Custom</button>
            </div>
            {inputs.cardMixMode === "advanced" && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CARD_BANDS.map((band) => (
                    <PercentInput key={band.key} label={band.label} name={`mix_${band.key}`} value={inputs[`mix_${band.key}`]} onChange={handleChange} placeholder={`${DEFAULT_MIX_ONLINE[band.key]}`} testId={`ol-mix-${band.key}`} />
                  ))}
                </div>
                <div className={`text-xs font-semibold text-center ${mixError ? "text-red-500" : "text-brand-aqua"}`} data-testid="ol-mix-total">
                  {`Total: ${mixTotal}% ${mixError ? "— must equal 100%" : ""}`}
                </div>
              </>
            )}
          </div>
        )}

        {!hasMinInput && (
          <div className="flex items-start gap-2 text-xs text-brand-grey bg-gray-50 rounded-xl p-3">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            Enter your monthly turnover and average order value to see your estimate.
          </div>
        )}

        {results && <ResultsPanel results={results} />}
        {results && (
          <div className="border-t border-brand-light-grey/60 pt-6">
            <h4 className="font-heading text-lg font-bold text-brand-black text-center mb-1">Want a tailored quote?</h4>
            <p className="text-xs text-brand-grey text-center mb-4">Send us your details and we&apos;ll prepare a personalised quote for your online payment setup.</p>
            <LeadForm schema={quoteOnline} context={leadContext} variant="compact" />
          </div>
        )}

        <p className="text-[11px] text-brand-grey leading-relaxed text-center">
          This estimate is based on the information you provided and typical online pricing assumptions. It is for illustration only and does not constitute a formal quote. A tailored quote may vary based on your exact business profile, card mix, transaction values, gateway setup, and processing behaviour.
        </p>
      </div>
    </div>
  );
}
